import { Separator } from "app/shadcnComponents/ui/separator";
import { getS } from "utils/helperFunctions";
import Button from "app/storybookComponents/Button";
import { useState } from "react";
import { useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import ReassignmentBox from "./ReassignmentBox";
import {
  ChangesSummary,
  DeactivateUserDepartmentSetupMap,
  ErrorMap,
} from "./types";

interface Props {
  userAccountIdToDeactivate?: number | null;
  departmentIds: number[];
  onStepForward: () => void;
  onStepBack: () => void;
  onUpdateChangesSummary?: (changesSummary: ChangesSummary) => void;
  initialDepartmentSetupMap?: DeactivateUserDepartmentSetupMap;
}
const ReassignDepartmentLeadersStep = ({
  userAccountIdToDeactivate,
  departmentIds,
  onStepForward,
  onStepBack,
  onUpdateChangesSummary,
  initialDepartmentSetupMap,
}: Readonly<Props>) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const departmentsById = useAppSelector(selectDepartments);
  const user = userAccountIdToDeactivate
    ? usersById[userAccountIdToDeactivate]
    : null;
  const [departmentSetupMap, setDepartmentSetupMap] =
    useState<DeactivateUserDepartmentSetupMap>(initialDepartmentSetupMap ?? {});
  const [departmentErrorMap, setDepartmentErrorMap] = useState<ErrorMap>({});
  const userName = `${user?.firstName} ${user?.lastName}`.trim() || "User";

  const clearDepartmentError = (departmentId: number) => {
    if (departmentErrorMap[departmentId]) {
      setDepartmentErrorMap((prev) => {
        const { [departmentId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const getReassignmentBoxes = () => {
    return departmentIds.map((departmentId) => {
      const department = departmentsById[departmentId];
      return (
        <ReassignmentBox
          key={departmentId}
          title={department?.name ?? ""}
          selectedUserAccountId={
            departmentSetupMap[departmentId]?.newDepartmentLeaderUserAccountId
          }
          setSelectedUserAccountId={(userAccountId) => {
            setDepartmentSetupMap({
              ...departmentSetupMap,
              [departmentId]: {
                ...departmentSetupMap[departmentId],
                newDepartmentLeaderUserAccountId: userAccountId,
              },
            });
            clearDepartmentError(departmentId);
          }}
          isAlternativeOptionSelected={
            departmentSetupMap[departmentId]?.removeDepartmentLeader
          }
          onAlternativeOptionSelected={(isAlternativeOptionSelected) => {
            setDepartmentSetupMap({
              ...departmentSetupMap,
              [departmentId]: {
                ...departmentSetupMap[departmentId],
                removeDepartmentLeader: isAlternativeOptionSelected,
              },
            });
            clearDepartmentError(departmentId);
          }}
          labels={{
            primaryLabel: {
              title: "Yes, assign new leader now",
              description: "Recommended to avoid broken analytics",
            },
            secondaryLabel: {
              title: "Skip for now",
              description: "You can assign later from Department Settings",
            },
          }}
          error={departmentErrorMap[departmentId]}
          excludeUserAccountIds={
            userAccountIdToDeactivate ? [userAccountIdToDeactivate] : []
          }
        />
      );
    });
  };

  const onBackClick = () => {
    onStepBack();
  };

  const getDepartmentValidationErrors = (): ErrorMap => {
    const errors: ErrorMap = {};
    departmentIds.forEach((departmentId) => {
      const departmentSetupState = departmentSetupMap[departmentId];
      if (
        !departmentSetupState ||
        (!departmentSetupState.removeDepartmentLeader &&
          !departmentSetupState.newDepartmentLeaderUserAccountId)
      ) {
        errors[departmentId] = {
          errorMessage:
            "*You need to select a department leader or skip this department",
        };
      }
    });
    return errors;
  };

  const onContinueClick = async () => {
    // Checking for validation errors
    const errors = getDepartmentValidationErrors();
    setDepartmentErrorMap(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    onUpdateChangesSummary?.({
      departmentChanges: departmentSetupMap,
    });
    onStepForward();
  };

  return (
    <>
      <p>
        <b>
          {userName} leads {departmentIds.length} department
          {getS(departmentIds.length)}.
        </b>{" "}
        What should happen to {departmentIds.length === 1 ? "this" : "these"}{" "}
        department{getS(departmentIds.length)}?
      </p>
      {getReassignmentBoxes()}
      <Separator />
      <div className="flex justify-end gap-2">
        <Button onClick={onBackClick} variant="secondary-gray">
          Back
        </Button>
        <Button onClick={onContinueClick}>Continue</Button>
      </div>
    </>
  );
};

export default ReassignDepartmentLeadersStep;
